/**
 * Roster Calculations - Pure utility functions for roster calculations
 * 
 * This module contains all the pure calculation functions and constants
 * used throughout the Roster feature. These functions have no side effects
 * and can be easily tested.
 */

import dayjs from 'dayjs';

// Constants
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const EMPTY_MONTHLY_ALLOCATIONS = Array(12).fill(0);

export const FUNCTION_OPTIONS = [
  'Developer',
  'QA Engineer', 
  'DevOps Engineer',
  'Product Manager',
  'Project Manager',
  'Business Analyst',
  'UX/UI Designer',
  'Data Analyst',
  'Security Specialist',
  'Technical Lead',
  'Architect',
  'Other'
];

export const DEVELOPER_FOCUS_OPTIONS = [
  'Frontend',
  'Backend', 
  'Full Stack',
  'Mobile',
  'Data',
  'ML/AI',
  'DevOps',
  'Security'
];

export const CA_DEPARTMENT_OPTIONS = [
  'Technology',
  'Product',
  'Data & Analytics', 
  'Security',
  'Infrastructure',
  'Other'
];

export const DEFAULT_COMPANY_OPTIONS = ['CA', 'US', 'Other'];
export const DEFAULT_LOCATION_OPTIONS = [
  { code: 'US', display: 'United States' },
  { code: 'CA', display: 'Canada' }
];

// Utility Functions

/**
 * Safe string conversion with fallback
 */
export function safeString(value, fallback = '') {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value).trim();
}

/**
 * Calculate total allocation across all months
 */
export function calculateTotalAllocation(monthlyAllocations) {
  if (!Array.isArray(monthlyAllocations)) {
    return 0;
  }
  return monthlyAllocations.reduce((sum, val) => sum + (val || 0), 0);
}

/**
 * Calculate maximum allocation in any single month
 */
export function calculateMaxAllocation(monthlyAllocations) {
  if (!Array.isArray(monthlyAllocations)) {
    return 0;
  }
  return Math.max(...monthlyAllocations.map(val => val || 0));
}

/**
 * Determine allocation status based on maximum allocation
 */
export function determineAllocationStatus(maxAllocation) {
  if (maxAllocation > 100) return 'Over Allocated';
  if (maxAllocation === 100) return 'Fully Allocated';
  if (maxAllocation > 0) return 'Under Allocated';
  return 'Not Allocated';
}

/**
 * Check if member has over-allocation
 */
export function hasOverAllocation(monthlyAllocations) {
  const maxAllocation = calculateMaxAllocation(monthlyAllocations);
  return {
    hasOverAllocation: maxAllocation > 100,
    maxAllocation
  };
}

/**
 * Format percentage for display
 */
export function formatPercentage(value, decimals = 1) {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0%';
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount, currency = 'USD') {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0';
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Get location display name from code
 */
export function getLocationDisplayName(locationCode) {
  const locationMap = {
    'US': 'United States',
    'CA': 'Canada',
    'UK': 'United Kingdom',
    'AU': 'Australia',
    'DE': 'Germany',
    'FR': 'France',
    'IN': 'India',
    'SG': 'Singapore'
  };
  
  return locationMap[locationCode] || locationCode || 'Unknown';
}

/**
 * Validate date range object
 */
export function validateDateRange(dateRange) {
  if (!dateRange || typeof dateRange !== 'object') {
    return { isValid: false, error: 'Invalid date range object' };
  }
  
  const { startMonth, endMonth, allocation } = dateRange;
  
  if (!startMonth || !endMonth) {
    return { isValid: false, error: 'Missing start or end month' };
  }
  
  if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) {
    return { isValid: false, error: 'Month values must be between 1 and 12' };
  }
  
  if (startMonth > endMonth) {
    return { isValid: false, error: 'Start month cannot be after end month' };
  }
  
  if (allocation !== undefined && (allocation < 0 || allocation > 100)) {
    return { isValid: false, error: 'Allocation must be between 0 and 100' };
  }
  
  return { isValid: true };
}

/**
 * Convert project-relative months to calendar months
 */
export function convertProjectRelativeToCalendar(projectRelativeMonth, projectStartMonth) {
  if (!projectStartMonth) {
    console.warn('convertProjectRelativeToCalendar: No project start month provided');
    return Math.max(1, Math.min(12, projectRelativeMonth));
  }
  
  try {
    const projectStart = dayjs(projectStartMonth + '-01');
    const targetDate = projectStart.add(projectRelativeMonth - 1, 'month');
    return targetDate.month() + 1; // Convert to 1-based calendar month
  } catch (error) {
    console.warn('convertProjectRelativeToCalendar: Error converting date:', error);
    return Math.max(1, Math.min(12, projectRelativeMonth));
  }
}

/**
 * Detect date range format (calendar vs project-relative)
 */
export function detectDateRangeFormat(dateRange, projectStartMonth) {
  if (!dateRange) {
    return 'unknown';
  }
  
  // Explicit version markers
  if (dateRange.timelineVersion === '3.0' || dateRange.timelineVersion === '2.0') {
    return 'calendar';
  }
  
  if (dateRange.useProjectRelative === true) {
    return 'project-relative';
  }
  
  if (dateRange.useProjectRelative === false) {
    return 'calendar';
  }
  
  // Heuristic detection
  const { startMonth, endMonth } = dateRange;
  
  if (!projectStartMonth) {
    return 'calendar'; // Default to calendar if no project context
  }
  
  if (startMonth >= 1 && startMonth <= 12 && endMonth >= 1 && endMonth <= 12 && startMonth <= endMonth) {
    return 'calendar'; // Looks like valid calendar months
  }
  
  return 'project-relative';
}

/**
 * Create mock allocation data for testing/fallback
 */
export function createMockAllocationData(outcomes) {
  if (!Array.isArray(outcomes) || outcomes.length === 0) {
    return [];
  }
  
  return outcomes.slice(0, 3).map((outcome, index) => ({
    outcome_id: outcome.id || `mock-outcome-${index}`,
    outcome_name: outcome.name || `Mock Outcome ${index + 1}`,
    applications: outcome.applications || ['Sample App'],
    staffed_roles: [
      {
        member_id: `mock-member-${index}`,
        role_name: 'Developer',
        milestone_name: 'Development',
        monthly_allocations: [50, 50, 50, 50, 50, 50, 0, 0, 0, 0, 0, 0],
        dateRanges: [{ startMonth: 1, endMonth: 6, allocation: 50 }]
      }
    ]
  }));
}

/**
 * Get filtered roster for role assignment
 */
export function getFilteredRosterForRole(roster, requiredFunction, requiredFocus, excludeMemberId) {
  if (!Array.isArray(roster)) {
    return [];
  }
  
  return roster.filter(member => {
    // Exclude specific member if provided
    if (excludeMemberId && member.id === excludeMemberId) {
      return false;
    }
    
    // Filter by function if specified
    if (requiredFunction && member.function !== requiredFunction) {
      return false;
    }
    
    // Filter by focus if specified (for developers)
    if (requiredFocus && member.focus !== requiredFocus) {
      return false;
    }
    
    return true;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Calculate member utilization statistics
 */
export function calculateMemberUtilization(monthlyAllocations) {
  if (!Array.isArray(monthlyAllocations)) {
    return {
      totalUtilization: 0,
      averageUtilization: 0,
      maxUtilization: 0,
      minUtilization: 0,
      utilizationByMonth: [],
      overUtilizedMonths: 0,
      underUtilizedMonths: 0,
      fullyUtilizedMonths: 0
    };
  }
  
  const validAllocations = monthlyAllocations.map(val => val || 0);
  const totalUtilization = validAllocations.reduce((sum, val) => sum + val, 0);
  const averageUtilization = validAllocations.length > 0 ? totalUtilization / validAllocations.length : 0;
  const maxUtilization = Math.max(...validAllocations);
  const minUtilization = Math.min(...validAllocations);
  
  let overUtilizedMonths = 0;
  let underUtilizedMonths = 0;
  let fullyUtilizedMonths = 0;
  
  validAllocations.forEach(allocation => {
    if (allocation > 100) {
      overUtilizedMonths++;
    } else if (allocation === 100) {
      fullyUtilizedMonths++;
    } else if (allocation > 0) {
      underUtilizedMonths++;
    }
  });
  
  return {
    totalUtilization,
    averageUtilization,
    maxUtilization,
    minUtilization,
    utilizationByMonth: validAllocations.map((allocation, index) => ({
      month: MONTHS[index],
      allocation,
      status: allocation > 100 ? 'over' : allocation === 100 ? 'full' : allocation > 0 ? 'under' : 'none'
    })),
    overUtilizedMonths,
    underUtilizedMonths,
    fullyUtilizedMonths
  };
}

/**
 * Generate allocation summary for a set of members
 */
export function generateAllocationSummary(members, getMemberOutcomeAllocations) {
  const summary = {
    totalMembers: members.length,
    allocatedMembers: 0,
    unallocatedMembers: 0,
    overAllocatedMembers: 0,
    fullyAllocatedMembers: 0,
    underAllocatedMembers: 0,
    totalOutcomes: new Set(),
    totalApplications: new Set(),
    monthlyTotals: [...EMPTY_MONTHLY_ALLOCATIONS],
    utilizationStats: {
      averageUtilization: 0,
      maxUtilization: 0,
      totalCapacity: members.length * 100 * 12, // 100% * 12 months per member
      usedCapacity: 0
    }
  };
  
  let totalUsedCapacity = 0;
  
  members.forEach(member => {
    const memberOutcomes = getMemberOutcomeAllocations(member.id);
    const hasAllocations = Object.keys(memberOutcomes).length > 0;
    
    if (hasAllocations) {
      summary.allocatedMembers++;
      
      // Calculate member's monthly totals
      const memberMonthlyTotals = [...EMPTY_MONTHLY_ALLOCATIONS];
      
      Object.values(memberOutcomes).forEach(outcome => {
        summary.totalOutcomes.add(outcome.outcome_name);
        outcome.applications.forEach(app => summary.totalApplications.add(app));
        
        outcome.staffed.forEach((allocation, monthIndex) => {
          memberMonthlyTotals[monthIndex] += allocation;
          summary.monthlyTotals[monthIndex] += allocation;
        });
      });
      
      // Determine member's allocation status
      const maxAllocation = calculateMaxAllocation(memberMonthlyTotals);
      if (maxAllocation > 100) {
        summary.overAllocatedMembers++;
      } else if (maxAllocation === 100) {
        summary.fullyAllocatedMembers++;
      } else {
        summary.underAllocatedMembers++;
      }
      
      // Add to total used capacity
      totalUsedCapacity += calculateTotalAllocation(memberMonthlyTotals);
    } else {
      summary.unallocatedMembers++;
    }
  });
  
  // Calculate utilization statistics
  summary.utilizationStats.usedCapacity = totalUsedCapacity;
  summary.utilizationStats.averageUtilization = summary.utilizationStats.totalCapacity > 0 ? 
    (totalUsedCapacity / summary.utilizationStats.totalCapacity) * 100 : 0;
  summary.utilizationStats.maxUtilization = calculateMaxAllocation(summary.monthlyTotals);
  
  // Convert sets to counts
  summary.totalOutcomes = summary.totalOutcomes.size;
  summary.totalApplications = summary.totalApplications.size;
  
  return summary;
}

/**
 * Validate member data structure
 */
export function validateMemberData(member) {
  const errors = [];
  const warnings = [];
  
  // Required fields
  if (!member.id) errors.push('Missing member ID');
  if (!member.name || member.name.trim() === '') errors.push('Missing member name');
  if (!member.company) warnings.push('Missing company');
  if (!member.location) warnings.push('Missing location');
  if (!member.function) warnings.push('Missing function');
  
  // Data type validation
  if (member.tags && !Array.isArray(member.tags)) {
    warnings.push('Tags should be an array');
  }
  
  // Business logic validation
  if (member.function === 'Developer' && !member.focus) {
    warnings.push('Developer should have a focus area specified');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasWarnings: warnings.length > 0
  };
}

/**
 * Sort members by various criteria
 */
export function sortMembers(members, sortBy = 'name', sortDirection = 'asc') {
  const sortedMembers = [...members];
  
  sortedMembers.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
        break;
      case 'company':
        aValue = (a.company || '').toLowerCase();
        bValue = (b.company || '').toLowerCase();
        break;
      case 'function':
        aValue = (a.function || '').toLowerCase();
        bValue = (b.function || '').toLowerCase();
        break;
      case 'maxAllocation':
        aValue = a.maxAllocation || 0;
        bValue = b.maxAllocation || 0;
        break;
      case 'outcomeCount':
        aValue = a.outcomeNames?.length || 0;
        bValue = b.outcomeNames?.length || 0;
        break;
      default:
        aValue = (a[sortBy] || '').toString().toLowerCase();
        bValue = (b[sortBy] || '').toString().toLowerCase();
    }
    
    if (sortDirection === 'desc') {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    } else {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
  });
  
  return sortedMembers;
}

/**
 * Filter members by multiple criteria
 */
export function filterMembers(members, filters) {
  return members.filter(member => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const searchableText = [
        member.name,
        member.company,
        member.function,
        member.role,
        member.focus,
        member.department
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(searchTerm)) {
        return false;
      }
    }
    
    // Company filter
    if (filters.companies && filters.companies.length > 0) {
      if (!filters.companies.includes(member.company)) {
        return false;
      }
    }
    
    // Location filter
    if (filters.locations && filters.locations.length > 0) {
      if (!filters.locations.includes(member.location)) {
        return false;
      }
    }
    
    // Function filter
    if (filters.functions && filters.functions.length > 0) {
      if (!filters.functions.includes(member.function)) {
        return false;
      }
    }
    
    // Allocation status filter
    if (filters.allocationStatuses && filters.allocationStatuses.length > 0) {
      if (!filters.allocationStatuses.includes(member.allocationStatus)) {
        return false;
      }
    }
    
    // Tag filter
    if (filters.tags && filters.tags.length > 0) {
      const memberTagNames = Array.from(member.memberTags || []);
      const hasMatchingTag = filters.tags.some(filterTag => 
        memberTagNames.includes(filterTag)
      );
      if (!hasMatchingTag) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Calculate team composition statistics
 */
export function calculateTeamComposition(members) {
  const composition = {
    byCompany: {},
    byLocation: {},
    byFunction: {},
    byFocus: {},
    byDepartment: {},
    totalMembers: members.length
  };
  
  members.forEach(member => {
    // Company composition
    const company = member.company || 'Unknown';
    composition.byCompany[company] = (composition.byCompany[company] || 0) + 1;
    
    // Location composition
    const location = getLocationDisplayName(member.location);
    composition.byLocation[location] = (composition.byLocation[location] || 0) + 1;
    
    // Function composition
    const func = member.function || 'Other';
    composition.byFunction[func] = (composition.byFunction[func] || 0) + 1;
    
    // Focus composition (for developers)
    if (member.function === 'Developer' && member.focus) {
      composition.byFocus[member.focus] = (composition.byFocus[member.focus] || 0) + 1;
    }
    
    // Department composition
    if (member.department) {
      composition.byDepartment[member.department] = (composition.byDepartment[member.department] || 0) + 1;
    }
  });
  
  return composition;
}